import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Plus, Trash, X as Close } from 'lucide-react';

interface TokenPurchaseProps {
  onClose: () => void;
  onSuccess: (tokens: number) => void;
}

const packages = [
  {
    tokens: 750_000,
    minutes: 3,
    price: 4
  },
  {
    tokens: 3_000_000,
    minutes: 12,
    price: 9,
    popular: true,
    savings: '25%'
  },
  {
    tokens: 8_000_000,
    minutes: 30,
    price: 19,
    savings: '36%'
  }
];

const savedCards = [
  {
    id: 'card-1',
    last4: '4242',
    brand: 'visa',
    expiryMonth: '12',
    expiryYear: '24',
    isDefault: true
  },
  {
    id: 'card-2',
    last4: '8210',
    brand: 'mastercard',
    expiryMonth: '08',
    expiryYear: '25'
  }
];

export function TokenPurchase({ onClose, onSuccess }: TokenPurchaseProps) {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(packages[1]);
  const [selectedCard, setSelectedCard] = useState<string>(savedCards[0].id);
  const [showCardForm, setShowCardForm] = useState(false);
  
  const [newCard, setNewCard] = useState({
    number: '',
    expiry: '',
    cvc: '',
    name: ''
  });

  const handlePurchase = async () => {
    setIsProcessing(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      // Call onSuccess with the purchased tokens
      if (onSuccess) {
        onSuccess(selectedPackage.tokens);
      }
    } catch (error) {
      console.error('Error processing purchase:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  if (isProcessing) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="theme-card p-6 max-w-sm w-full relative overflow-hidden text-center"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-50" />
          <div className="relative space-y-4">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              className="w-8 h-8 mx-auto text-primary"
            >
              <svg className="animate-spin" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            </motion.div>
            <h3 className="text-xl font-semibold">Processing Payment</h3>
            <p className="text-sm text-muted-foreground">Please wait...</p>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="theme-card w-full max-w-md relative flex flex-col max-h-[85vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-4 border-b border-border flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Get Talk Time</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Package Selection */}
          <div className="grid grid-cols-3 gap-2">
            {packages.map((pkg) => (
              <button
                key={pkg.tokens}
                onClick={() => setSelectedPackage(pkg)}
                className={`p-3 rounded-lg text-center relative group overflow-hidden ${
                  selectedPackage.tokens === pkg.tokens
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/70'
                }`}
              >
                <div className="text-base font-semibold">₹{pkg.price}</div>
                <div className="text-xs opacity-80">{pkg.minutes}m</div>
                {pkg.popular && (
                  <div className="absolute -top-1 -right-1 bg-warning/20 text-warning text-[10px] px-1.5 py-0.5 rounded-full">
                    Popular
                  </div>
                )}
                {pkg.savings && (
                  <div className="absolute bottom-1 right-1 text-[10px] text-success">
                    Save {pkg.savings}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Payment Selection */}
          {!showCardForm ? (
            <div className="space-y-2">
              {savedCards.map((card) => (
                <button
                  key={card.id}
                  onClick={() => setSelectedCard(card.id)}
                  className={`w-full p-2 rounded-lg flex items-center justify-between ${
                    selectedCard === card.id
                      ? 'bg-primary/10 border border-primary'
                      : 'bg-muted hover:bg-muted/70'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded bg-muted/50 flex items-center justify-center">
                      {card.brand === 'visa' ? 'V' : 'M'}
                    </div>
                    <span className="text-sm">•••• {card.last4}</span>
                  </div>
                  {selectedCard === card.id && <div className="w-2 h-2 rounded-full bg-primary" />}
                </button>
              ))}
              <button
                onClick={() => setShowCardForm(true)}
                className="w-full p-2 rounded-lg bg-muted hover:bg-muted/70 flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                <span className="text-sm">Add New Card</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">New Card</span>
                <button
                  onClick={() => setShowCardForm(false)}
                  className="text-xs text-primary"
                >
                  Cancel
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Card Number"
                  className="col-span-2 px-3 py-2 bg-muted rounded-lg text-sm"
                />
                <input
                  type="text"
                  placeholder="MM/YY"
                  className="px-3 py-2 bg-muted rounded-lg text-sm"
                />
                <input
                  type="text"
                  placeholder="CVC"
                  className="px-3 py-2 bg-muted rounded-lg text-sm"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border shrink-0">
          <div className="text-[10px] text-center text-muted-foreground mb-4">
            Secure payment powered by Stripe
          </div>
          <button
            onClick={handlePurchase}
            className="w-full theme-button py-3 rounded-lg flex items-center justify-center gap-2"
          >
            Pay ₹{selectedPackage.price}
          </button>
        </div>
      </motion.div>
    </div>
  );
}